import React, { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';

const kindOf = (file = '') => {
  if (/\.pdf$/i.test(file)) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|bmp)$/i.test(file) || !/\.[a-z0-9]{2,5}$/i.test(file)) return 'image';
  return 'other';
};

/**
 * Button + dialog to view files stored under the ERP's /uploads/ folder
 * (payment proofs, receipts). Images render inline, PDFs in a frame, anything
 * else as a link.
 *
 * Older proofs were saved without an extension; they are images, so a file
 * with no extension is shown as an image.
 *
 * @param {Array<{file:string, caption?:string}>} files  filenames relative to uploads
 * @param {string} title        dialog title
 * @param {string} buttonLabel  defaults to "View" / "View (n)"
 * @param {string} baseUrl      defaults to REACT_APP_UPLOADS_URL
 */
export default function UploadedFilesView({
  files = [],
  title = 'Files',
  buttonLabel,
  baseUrl = process.env.REACT_APP_UPLOADS_URL,
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  if (!files.length) {
    return <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>—</Typography>;
  }

  const current = files[Math.min(active, files.length - 1)];
  const url = `${baseUrl}${current.file}`;
  const kind = kindOf(current.file);

  return (
    <GlobalDialog
      title={title}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="md"
      closeButton
      popupButton={
        <Button
          size="small"
          variant="outlined"
          startIcon={<Icon icon="solar:gallery-linear" width={16} height={16} />}
          onClick={() => {
            setActive(0);
            setOpen(true);
          }}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999 }}
        >
          {buttonLabel || (files.length > 1 ? `View (${files.length})` : 'View')}
        </Button>
      }
      content={
        <Stack spacing={1.5}>
          {files.length > 1 && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {files.map((f, i) => (
                <Button
                  key={f.file}
                  size="small"
                  variant={i === active ? 'contained' : 'outlined'}
                  onClick={() => setActive(i)}
                  sx={{ textTransform: 'none', borderRadius: 999 }}
                >
                  {f.caption || `File ${i + 1}`}
                </Button>
              ))}
            </Stack>
          )}

          <Box sx={{ height: '65vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {kind === 'image' && (
              <Box component="img" src={url} alt={current.file} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            )}
            {kind === 'pdf' && (
              <Box component="iframe" src={url} title={current.file} sx={{ width: '100%', height: '100%', border: 'none' }} />
            )}
            {kind === 'other' && <Typography>Preview not available for this file type.</Typography>}
          </Box>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }} noWrap>
              {current.caption ? `${current.caption} · ` : ''}
              {current.file}
            </Typography>
            <Button
              size="small"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Icon icon="solar:square-top-down-linear" width={16} height={16} />}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Open in new tab
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
}
